import { NotificationTypeInfo } from '../notification-settings-list/graphql';
import { AnnouncementStatus, AnnouncementTargeting } from './graphql';

// #738: Uebersetzung der rohen Enum-Namen und die geteilten Helfer von Liste, Zeile und Formular.
//
// Anders als beim Katalog (label/categoryLabel) liefert das Backend fuer `status` KEINE uebersetzte
// Bezeichnung. Die Zuordnung Enum-Wert -> i18n-Schluessel liegt deshalb hier zentral, damit Zeile,
// Filter und der Vollstaendigkeitstest dieselbe Quelle benutzen.

export const ANNOUNCEMENT_I18N_PREFIX = 'core.announcement';

export const ANNOUNCEMENT_STATUS_VALUES: readonly AnnouncementStatus[] = Object.values(AnnouncementStatus);

/**
 * Die sieben unterstuetzten Locales — NotificationEngineProperties.supportedLocales.
 *
 * `sl` gehoert dazu, obwohl die Oberflaeche selbst nicht auf Slowenisch vorliegt und
 * user-preferred-language-dialog es (noch) nicht anbietet: seit der Inhalt einer Ankuendigung bei
 * JEDEM Lesen aufgeloest wird (Client-Dok §7.2), ist eine fehlende Sprachfassung kein einmaliger
 * Versandeffekt mehr — sie zeigt jedem slowenischen Leser dauerhaft die deutsche Rueckfallebene
 * (§8.3).
 */
export const ANNOUNCEMENT_LOCALES: readonly string[] = ['de', 'en', 'bg', 'cs', 'ro', 'sk', 'sl'];

/** Die einzige Sprache, die der Server erzwingt: `de` braucht einen nicht-leeren Titel. */
export const ANNOUNCEMENT_REQUIRED_LOCALE = 'de';

/**
 * Unbekannte Werte (neuer Server-Enum-Wert vor dem naechsten Web-Release) werden roh angezeigt,
 * statt als fehlender Uebersetzungsschluessel zu erscheinen.
 */
export function announcementStatusLabelKey(status: string | null | undefined): string {
  if (!status) {
    return '';
  }
  return ANNOUNCEMENT_STATUS_VALUES.includes(status as AnnouncementStatus)
    ? `${ANNOUNCEMENT_I18N_PREFIX}.status.${status}`
    : status;
}

export type TagSeverity = 'success' | 'info' | 'warning' | 'danger';

/**
 * p-tag-Severity je Status.
 *
 * CANCELLED ist `danger`, weil ein abgebrochener Versand die Aufmerksamkeit braucht — nicht weil
 * etwas kaputt waere. RUNNING ist `warning` im Sinne von „laeuft gerade, jetzt ist kein Abbruch mehr
 * moeglich".
 *
 * RETRACTED teilt sich `danger` mit CANCELLED, und das ist Absicht: fuer den Leser der Liste sind
 * beide dieselbe Klasse von Ereignis — diese Meldung gilt nicht. Ob sie je draussen war,
 * unterscheidet der Text, nicht die Farbe.
 */
export function announcementStatusSeverity(status: string | null | undefined): TagSeverity {
  switch (status) {
    case AnnouncementStatus.DONE:
      return 'success';
    case AnnouncementStatus.CANCELLED:
    case AnnouncementStatus.RETRACTED:
      return 'danger';
    case AnnouncementStatus.RUNNING:
      return 'warning';
    default:
      return 'info';
  }
}

// Die vier folgenden Praedikate spiegeln die Allow-List-switches aus AnnouncementAdminServiceImpl.
// Sie sind die einzige Quelle dafuer, welche Aktion eine Zeile anbietet — eine Luecke darin bietet
// dem Nutzer etwas an, das der Server ablehnt.

/** Abbrechen geht ausschliesslich solange SCHEDULED — danach ist der Fan-out nicht mehr aufhaltbar. */
export function isCancellable(status: string | null | undefined): boolean {
  return status === AnnouncementStatus.SCHEDULED;
}

/**
 * Den INHALT aendern geht in SCHEDULED, RUNNING und DONE — der Server lehnt CANCELLED und
 * RETRACTED ab („announcement is not editable in status …").
 *
 * Zielgruppe und Termin sind zusaetzlich nur solange SCHEDULED aenderbar. Das entscheidet nicht
 * dieses Praedikat, sondern das Formular: hier geht es nur darum, ob die Aktion ueberhaupt
 * angeboten wird.
 */
export function isEditable(status: string | null | undefined): boolean {
  return status === AnnouncementStatus.SCHEDULED
    || status === AnnouncementStatus.RUNNING
    || status === AnnouncementStatus.DONE;
}

/**
 * RETRACTED ist absichtlich dabei: ein zweiter Aufruf ist die WIEDERAUFNAHME eines
 * unvollstaendigen Rueckzugs, nicht ein Fehler — es gibt keinen Hintergrundjob, der einen
 * abgebrochenen Lauf nachholt (Client-Dok §8.3).
 *
 * SCHEDULED gehoert nicht hierher: solange nichts verschickt wurde, ist Abbrechen die richtige
 * Aktion.
 */
export function isRetractable(status: string | null | undefined): boolean {
  return status === AnnouncementStatus.RUNNING
    || status === AnnouncementStatus.DONE
    || status === AnnouncementStatus.RETRACTED;
}

/**
 * Nur RUNNING/DONE. Eine zurueckgezogene Ankuendigung laesst sich NIE mehr richtigstellen — der
 * Rueckzug nimmt sie aus jedem Posteingang, eine Richtigstellung dazu ginge ins Leere. Umgekehrt
 * laesst sich eine richtiggestellte jederzeit noch zurueckziehen.
 *
 * Das ist eine Einbahnstrasse, die man im Vorfall versehentlich nimmt: die Oberflaeche muss sie
 * VOR dem Klick auf „Zurueckziehen" ansprechen, nicht erst ueber die Fehlermeldung danach.
 */
export function isCorrectable(status: string | null | undefined): boolean {
  return status === AnnouncementStatus.RUNNING || status === AnnouncementStatus.DONE;
}

/**
 * Kategorie-Erkennung fuer die Typ-Auswahl.
 *
 * Der Katalog liefert `categoryKey`; welchen WERT der Server dort schickt — `announcement` oder
 * `notification.category.announcement` — ist frontendseitig nicht belegbar (die #735-Testfixture
 * benutzt 'announcement', das ist aber ein erfundener Wert). Deshalb tolerant: beide Schreibweisen
 * treffen, und zusaetzlich der typeKey-Praefix, den die Engine ohnehin garantiert
 * (announcement.maintenance / announcement.product).
 */
export const ANNOUNCEMENT_CATEGORY_KEY = 'announcement';

export function matchesAnnouncementCategory(type: NotificationTypeInfo): boolean {
  const category = type?.categoryKey ?? '';
  return (
    category === ANNOUNCEMENT_CATEGORY_KEY ||
    category.endsWith(`.${ANNOUNCEMENT_CATEGORY_KEY}`) ||
    (type?.typeKey ?? '').startsWith(`${ANNOUNCEMENT_CATEGORY_KEY}.`)
  );
}

/**
 * Typ-Auswahl aus dem Katalog.
 *
 * Greift der Kategoriefilter nicht (weil der Server einen dritten Wert schickt), bleibt die VOLLE
 * Katalogliste stehen. Eine leere Auswahl wuerde das Formular unbenutzbar machen; der Server lehnt
 * einen nicht-content-basierten typeKey ohnehin mit ValidationProblem ab, das ist der mildere Fehler.
 */
export function announcementTypeOptions(catalog: NotificationTypeInfo[]): NotificationTypeInfo[] {
  const types = catalog ?? [];
  const filtered = types.filter(matchesAnnouncementCategory);
  return filtered.length > 0 ? filtered : types;
}

/**
 * E-Mail-Vorgabe je Typ, als i18n-Schluessel.
 *
 * Der Unterschied ist rechtlich begruendet und wirkt direkt auf die Reichweite: eine
 * Produktankuendigung erreicht per E-Mail nur, wer sie ausdruecklich aktiviert hat. Das muss bei der
 * Typwahl sichtbar sein, damit niemand eine Produktnachricht verschickt und sich ueber wenige Mails
 * wundert.
 */
export const ANNOUNCEMENT_TYPE_PRODUCT = 'announcement.product';
export const ANNOUNCEMENT_TYPE_MAINTENANCE = 'announcement.maintenance';

/**
 * Client-Dok §8.3: `dashboardUntil` darf die Lebensdauer der Nachricht nicht ueberschreiten — je
 * Typ verschieden, weil die TTL selbst es ist (18-announcement-lifecycle.md).
 */
export const ANNOUNCEMENT_DASHBOARD_TTL_DAYS: Record<string, number> = {
  [ANNOUNCEMENT_TYPE_MAINTENANCE]: 7,
  [ANNOUNCEMENT_TYPE_PRODUCT]: 30
};

export function emailDefaultHintKey(typeKey: string | null | undefined): string | null {
  switch (typeKey) {
    case ANNOUNCEMENT_TYPE_MAINTENANCE:
      return `${ANNOUNCEMENT_I18N_PREFIX}.form.emailDefaultOn`;
    case ANNOUNCEMENT_TYPE_PRODUCT:
      return `${ANNOUNCEMENT_I18N_PREFIX}.form.emailDefaultOptIn`;
    default:
      // Ein kuenftiger dritter Typ bekommt keinen erfundenen Hinweis.
      return null;
  }
}

/** Kurzform der Zielgruppe fuer die Zeile. */
export interface TargetingSummary {
  allTenants: boolean;
  tenantCount: number;
  roleCount: number;
}

/**
 * targeting ist ein pass-through-Map-Scalar und kann alles enthalten — auch Formen, die dieses
 * Frontend nie erzeugt hat (ein aelterer Datensatz, ein anderer Client). Der Zugriff ist deshalb
 * durchgehend defensiv; eine Zeile darf an einer unerwarteten Map nicht zerbrechen.
 */
export function summarizeTargeting(targeting: AnnouncementTargeting | null | undefined): TargetingSummary {
  const tenantIds = Array.isArray(targeting?.tenantIds) ? targeting.tenantIds : [];
  const roles = Array.isArray(targeting?.roles) ? targeting.roles : [];
  return {
    allTenants: targeting?.allTenants === true,
    tenantCount: tenantIds.length,
    roleCount: roles.length
  };
}
